import { useEffect, useState, useCallback } from 'react';
import { getUsers, deleteUser, createUser } from '../services/api';
import { FaCheckCircle, FaPlus, FaTimesCircle, FaTrash } from 'react-icons/fa';
import StandardTable from '../components/StandardTable';

const UserList = () => {
    const [users, setUsers] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [search, setSearch] = useState('');
    const [sortColumn, setSortColumn] = useState('email');
    const [sortDirection, setSortDirection] = useState('asc');
    const [showModal, setShowModal] = useState(false);
    const [formMessage, setFormMessage] = useState(null);
    const [newUser, setNewUser] = useState({
        name: '',
        email: '',
        password: '',
        is_admin: false,
        is_active: false,
    });

    // Fetch users
    const fetchUsers = useCallback(async (page = 1) => {
        setLoading(true);
        try {
            const res = await getUsers({
                page,
                per_page: perPage,
                search,
                sort_by: sortColumn,
                order: sortDirection
            });
            setUsers(res.data.map(user => ({ ...user, actions: [{ label: 'Delete', onClick: () => handleDelete(user.id) }] })));
            setTotalRows(res.total);
            setCurrentPage(page);
        } catch (err) {
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [perPage, search, sortColumn, sortDirection]);

    // Initial + search/sort fetch
    useEffect(() => {
        fetchUsers(currentPage);
    }, [fetchUsers]);

    // Debounce search
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            fetchUsers(1);
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [search]);

    const handlePageChange = page => {
        fetchUsers(page);
    };

    const handleSort = (column, direction) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(direction);
        }
    };

    const handleSearchChange = (e) => {
        setSearch(e.target.value);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;
        try {
            await deleteUser(id);
            fetchUsers(currentPage);
        } catch (err) {
            console.error(err.message);
        }
    };

    const handleRowsPerPageChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        fetchUsers(page);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewUser((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();

        // Example: basic frontend validation
        if (!newUser.email || !newUser.password) {
            setFormMessage({ type: 'error', text: 'Email and Password are required.' });
            return;
        }

        try {
            const response = await createUser(newUser);

            if (response.status_code === 201) {
                setFormMessage({ type: 'success', text: response.message || 'User added successfully!' });

                // Load users again
                fetchUsers(1);
                // Reset fields, but keep modal open
                setNewUser({ name: '', email: '', password: '', is_admin: false, is_staff: false, is_active: true });
            } else {
                setFormMessage({ type: 'error', text: response.message || 'Something went wrong.' });
            }
        } catch (error) {
            setFormMessage({ type: 'error', text: error.response?.data?.message || 'Error adding user.' });
        }
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.name || '-',
            sortable: true,
            sortField: 'name',
        },
        {
            name: 'Email',
            selector: row => row.email || '-',
            sortable: true,
            sortField: 'email',
        },
        {
            name: 'Admin',
            selector: row => row.is_admin ? (<span className="text-success"><FaCheckCircle/></span>) : (<span className="text-danger"><FaTimesCircle/></span>),
            sortable: true,
        },
        {
            name: 'Active',
            selector: row => row.is_active ? (<span className="text-success"><FaCheckCircle/></span>) : (<span className="text-danger"><FaTimesCircle/></span>),
            sortable: true,
        },
        {
            name: 'Created',
            selector: row => row.created_at,
            sortable: true,
        },
        {
            name: 'Actions',
        },
    ];

    return (
        <div className="mt-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2>Users List</h2>
                <button className="btn btn-primary" style={{ backgroundColor: '#5865F2' }} onClick={() => setShowModal(true)}>
                    <FaPlus/> Add New User
                </button>
            </div>
            <StandardTable
                columns={columns}
                data={users}
                search={search}
                onSearchChange={(e) => setSearch(e.target.value)}
                loading={loading}
                paginationServer={true}
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handleRowsPerPageChange}
            />

            {/* Modal */}
            {showModal && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <form onSubmit={handleCreateUser}>
                                <div className="modal-header">
                                    <h5 className="modal-title">Add New User</h5>
                                    <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    {formMessage && (
                                        <div className={`alert ${formMessage.type === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
                                            {formMessage.text}
                                        </div>
                                    )}
                                    <div className="mb-3">
                                        <label>Name</label>
                                        <input
                                            type="text"
                                            name="name"
                                            className="form-control"
                                            value={newUser.name}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            name="email"
                                            className="form-control"
                                            value={newUser.email}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label>Password</label>
                                        <input
                                            type="password"
                                            name="password"
                                            className="form-control"
                                            value={newUser.password}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-check mb-2">
                                        <input
                                            type="checkbox"
                                            name="is_admin"
                                            className="form-check-input"
                                            checked={newUser.is_admin}
                                            onChange={handleInputChange}
                                        />
                                        <label className="form-check-label">Is Admin</label>
                                    </div>
                                    <div className="form-check mb-2">
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            className="form-check-input"
                                            checked={newUser.is_active}
                                            onChange={handleInputChange}
                                        />
                                        <label className="form-check-label">Is Active</label>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="submit" className="btn btn-success">Add</button>
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserList;
